// Medical Portal — Sample Concerns (TL-CSD-SCR)
//
// Data source: Trust OPM, through our backend
//   GET /api/sample-concerns?q=…&status=…&page=…
// TrustLab's accessioning staff at the lab set-up inside this hospital log a
// concern in OPM when a sample arrives without a label or proper
// information — a locked server time stamp and photographs. The hospital
// sees every concern raised against it here, with the TAT against TrustLab's
// targets (first reply 2 h, closure 24 h), and answers each one.
//
// Layout follows Report Queries: KPI tiles · filter row · status tabs ·
// card list with the TAT trail · pagination. Opening a card shows the record,
// its photos, the TAT box, the conversation trail and the reply form.
import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Skeleton from '@mui/material/Skeleton';
import Pagination from '@mui/material/Pagination';
import SearchIcon from '@mui/icons-material/Search';
import { MP, MONO, Pill, SectionCard, CardHeader, StatCard } from './shared';
import SampleConcernModal, { TatTrail, dur, stamp } from './SampleConcernModal';
import { useAuth } from '../../../context/AuthContext';
import { useListSampleConcernsQuery } from '../../../store/queriesApi';

// Tab key = the OPM status asked for.
const TABS = [
  { key: '',         label: 'All' },
  { key: 'Awaiting', label: 'Awaiting your reply' },
  { key: 'Replied',  label: 'Replied' },
  { key: 'Disputed', label: 'Disputed' },
  { key: 'Closed',   label: 'Closed' },
];

export const STATUS_TONE = { Awaiting: 'amber', Replied: 'blue', Disputed: 'red', Closed: 'green' };
export const STATUS_LABEL = {
  Awaiting: 'Awaiting your reply', Replied: 'You replied', Disputed: 'Disputed', Closed: 'Closed',
};

const SampleConcerns = () => {
  const { user } = useAuth();
  const clientId = user?.client_id;

  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [openNo, setOpenNo] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => { setQ(search.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // No polling. The old 60s interval kept calling OPM for anyone who left
  // this tab open, whether or not they were looking at it.
  //
  // `refetchOnMountOrArgChange` instead: opening the screen always asks.
  // The backend answers that from its warm copy straight away, so it is
  // cheap, and kicks its own refresh behind the response — which is how a
  // concern raised a minute ago turns up without reloading the page.
  const { data, isLoading, isFetching, isError, error, refetch } = useListSampleConcernsQuery(
    { q, status, page, per_page: 20 },
    { skip: !clientId, refetchOnMountOrArgChange: true },
  );

  // The backend sets `_stale` when it served that warm copy while fetching a
  // newer one. Come back once to collect it. One shot, not a loop: the next
  // answer is fresh, so `_stale` is false and this stops.
  useEffect(() => {
    if (!data?._stale) return undefined;
    const id = setTimeout(() => { refetch(); }, 2500);
    return () => clearTimeout(id);
  }, [data, refetch]);
  const records = useMemo(() => data?.records || [], [data]);
  const k = data?.meta?.kpis || {};
  const counts = k.counts || {};
  const pagination = data?.pagination || { current_page: 1, last_page: 1, total: 0 };
  const all = Object.values(counts).reduce((a, b) => a + b, 0);

  const pick = (key) => { setStatus(key); setPage(1); };

  return (
    <Box>
      {/* ── KPI tiles ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)', lg: 'repeat(7, 1fr)' }, gap: 1.5, mb: 1.75 }}>
        <StatCard label="Open concerns" value={k.open ?? '—'} tone="teal" />
        <StatCard label="Awaiting your reply" value={k.awaiting ?? '—'} tone={k.awaiting ? 'amber' : 'green'} />
        <StatCard label="Raised this month" value={k.month ?? '—'} tone="blue" />
        <StatCard label="Top issue this month" value={<Box component="span" sx={{ fontSize: 14, lineHeight: 1.3, display: 'block' }}>{k.top || '—'}</Box>} tone="purple" />
        <StatCard label={`Avg reply · target ${k.respH || 2} h`} value={dur(k.avgRespMin)}
          tone={k.avgRespMin == null ? 'grey' : k.avgRespMin > (k.respH || 2) * 60 ? 'red' : 'green'} />
        <StatCard label={`Avg to close · target ${k.closeH || 24} h`} value={dur(k.avgCloseMin)}
          tone={k.avgCloseMin == null ? 'grey' : k.avgCloseMin > (k.closeH || 24) * 60 ? 'red' : 'green'} />
        <StatCard label="TAT breaches" value={k.breaches ?? '—'} tone={k.breaches ? 'red' : 'green'} />
      </Box>

      {/* ── Filter row ── */}
      <Box sx={{ bgcolor: MP.white, border: `1px solid ${MP.border}`, borderRadius: MP.radius, boxShadow: MP.shadow, p: '13px', mb: 1.75 }}>
        <TextField
          size="small" fullWidth
          placeholder="Search by concern ID, ward, staff name, barcode or issue…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={filterInputSx}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: MP.textMuted }} /></InputAdornment>) }}
        />
      </Box>

      {/* ── Status tabs ── */}
      <Box sx={{ borderBottom: `1px solid ${MP.border}`, mb: 2, overflowX: 'auto', '&::-webkit-scrollbar': { height: 0 } }}>
        <Stack direction="row">
          {TABS.map(({ key, label }) => {
            const on = status === key;
            const n = key ? (counts[key] || 0) : all;
            return (
              <Box key={key || 'all'} onClick={() => pick(key)} sx={{
                px: '14px', py: '9px', fontSize: 13, fontWeight: on ? 600 : 500, cursor: 'pointer', whiteSpace: 'nowrap',
                color: on ? MP.teal : MP.textMuted, borderBottom: `2px solid ${on ? MP.teal : 'transparent'}`, mb: '-1px',
                display: 'flex', alignItems: 'center', gap: '6px', '&:hover': on ? {} : { color: MP.dark },
              }}>
                {label}
                {n > 0 && (
                  <Box component="span" sx={{ bgcolor: key === 'Awaiting' ? MP.amberLight : MP.borderSoft, color: key === 'Awaiting' ? MP.amber : MP.ink3, fontFamily: MONO, fontSize: 10.5, minWidth: 20, textAlign: 'center', px: '5px', py: '1px', borderRadius: '3px' }}>
                    {n}
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>

      {/* ── Register ── */}
      <SectionCard>
        <CardHeader
          title="Sample Concern Register"
          subtitle={pagination.total
            ? `Showing ${pagination.from}–${pagination.to} of ${pagination.total} · raised by TrustLab · from Trust OPM${isFetching && !isLoading ? ' · updating…' : ''}`
            : 'Samples TrustLab received from your wards without a label or proper information'}
        />
        <Stack spacing={1.25} sx={{ p: '15px' }}>
          {isLoading && Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={86} />
          ))}

          {!isLoading && records.map((r) => <ConcernCard key={r.id} r={r} onClick={() => setOpenNo(r.no)} />)}

          {!isLoading && records.length === 0 && (
            <Box sx={{ py: 6, textAlign: 'center', color: MP.textMuted, bgcolor: MP.white, border: `1px solid ${MP.border}`, borderRadius: '6px', fontSize: 13 }}>
              {!clientId
                ? 'Your account is missing a Client ID — contact admin to link one.'
                : isError
                  ? (error?.data?.message || 'Could not load your sample concerns.')
                  : (q || status) ? 'No concerns match the search or filter.' : 'No sample concerns raised against your hospital. 🎉'}
            </Box>
          )}

          {pagination.last_page > 1 && (
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent="space-between" spacing={1} sx={{ pt: 1 }}>
              <Typography sx={{ fontFamily: MONO, fontSize: 11.5, color: MP.textMuted }}>
                Page {pagination.current_page} of {pagination.last_page}
              </Typography>
              <Pagination count={pagination.last_page} page={pagination.current_page} onChange={(_, p) => setPage(p)}
                size="small" shape="rounded"
                sx={{ '& .MuiPaginationItem-root': { fontFamily: MONO, fontSize: 12 }, '& .Mui-selected': { bgcolor: `${MP.tealLight} !important`, color: MP.tealDark, fontWeight: 700 } }} />
            </Stack>
          )}
        </Stack>
      </SectionCard>

      <SampleConcernModal no={openNo} open={!!openNo} onClose={() => setOpenNo(null)} />
    </Box>
  );
};

// ── One concern in the register ─────────────────────────────────────────
const ConcernCard = ({ r, onClick }) => {
  const major = r.severity === 'Major';
  return (
    <Box onClick={onClick} sx={{
      bgcolor: MP.white, border: `1px solid ${MP.border}`, borderRadius: '6px', boxShadow: MP.shadow,
      position: 'relative', overflow: 'hidden', cursor: 'pointer', transition: 'all .18s',
      '&::before': { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: major ? MP.red : MP.amber },
      '&:hover': { borderColor: MP.teal, boxShadow: '0 2px 12px rgba(16,21,28,.10)' },
    }}>
      <Box sx={{
        display: 'grid', gap: 2, alignItems: 'center', px: 2.25, py: 1.75, pl: 2.75,
        gridTemplateColumns: { xs: '1fr', md: '210px 1.3fr 1fr minmax(240px, 1.4fr) 150px' },
      }}>
        <Box>
          <Typography sx={{ fontFamily: MONO, fontSize: 11.5, fontWeight: 700, color: MP.tealDark }}>{r.no}</Typography>
          <Typography sx={{ fontFamily: MONO, fontSize: 11, color: MP.textMuted }}>{stamp(r.raisedAt)}</Typography>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: MP.dark }} noWrap>
            {r.issues?.[0] || '—'}
            {r.issues?.length > 1 && <Box component="span" sx={{ fontSize: 12, fontWeight: 500, color: MP.textMuted }}> +{r.issues.length - 1} more</Box>}
          </Typography>
          <Typography sx={{ fontSize: 12, color: MP.textMuted }}>
            {r.count} sample{r.count === 1 ? '' : 's'} · {r.severity} · {r.messages} message{r.messages === 1 ? '' : 's'}
          </Typography>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, color: MP.dark }} noWrap>{r.ward}</Typography>
          <Typography sx={{ fontSize: 12, color: MP.textMuted }} noWrap>Handed by {r.handedBy}</Typography>
        </Box>
        <TatTrail r={r} />
        <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }} spacing={0.75}>
          <Pill tone={STATUS_TONE[r.status] || 'grey'}>{STATUS_LABEL[r.status] || r.status}</Pill>
          {major && <Pill tone="red">Major</Pill>}
        </Stack>
      </Box>
    </Box>
  );
};

const filterInputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: MP.white, borderRadius: '6px', fontSize: 13, fontFamily: "'Outfit', sans-serif",
    '& fieldset': { borderColor: MP.border, borderWidth: 1.5 },
    '&:hover fieldset': { borderColor: MP.teal },
    '&.Mui-focused fieldset': { borderColor: MP.teal, borderWidth: 1.5 },
  },
};

export default SampleConcerns;
